// © 2026 AttainIP Engineering
/**
 * Consent utilities
 *
 * Reads the cookie consent preferences stored by CookieConsent.tsx
 * and exposes helpers that other modules use to gate tracking.
 *
 * Keys and categories match the marketing site (attain-ss-public) so
 * consent carries across the shared origin.
 */

const COOKIE_PREFERENCES_KEY = 'cookie_preferences';

export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  performance: boolean;
  advertisement: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

/**
 * Read the current consent preferences from localStorage.
 * Returns defaults (all false except necessary) if nothing is stored yet.
 */
export function getConsentPreferences(): CookiePreferences {
  try {
    const raw = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!raw) return { ...defaultPreferences };
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    return {
      necessary: true, // always true
      functional: !!parsed.functional,
      analytics: !!parsed.analytics,
      performance: !!parsed.performance,
      advertisement: !!parsed.advertisement,
    };
  } catch {
    return { ...defaultPreferences };
  }
}

/** True when the user has granted Analytics consent */
export function hasAnalyticsConsent(): boolean {
  return getConsentPreferences().analytics;
}

/**
 * True unless the user has explicitly declined analytics consent.
 * Returns true when no consent decision has been made yet (pre-banner).
 */
export function isAnalyticsNotDeclined(): boolean {
  try {
    const raw = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!raw) return true; // No decision yet → allow by default
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    return parsed.analytics !== false;
  } catch {
    return true;
  }
}

/** True when the user has granted Advertisement consent */
export function hasAdvertisementConsent(): boolean {
  return getConsentPreferences().advertisement;
}
